import {useIntl} from 'react-intl'
import {CHATBOT_MODE, MessageItemCommonProps, MessageOrderInfo} from '../../definitions'
import {MessageItemWrapper} from './MessageItemWrapper'
import {useCallback, useMemo, useState} from 'react'
import {Button} from '../../../../../_gori/partials/widgets'
import {useOrderInfo} from '../../hooks'
import {AutocompleteAddress} from './AutocompleteAddress'
import clsx from 'clsx'
import {useChatbotContext} from '../../context'

type MessageItemConfirmOrderInfoProps = MessageItemCommonProps<MessageOrderInfo>

export const MessageItemConfirmOrderInfo = ({message}: MessageItemConfirmOrderInfoProps) => {
  const intl = useIntl()
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const {disabled, formik, addressError, setAddressError, resetForm} = useOrderInfo({message})
  const {mode: chatbotMode} = useChatbotContext()

  const isEditMode = useMemo(() => mode === 'edit', [mode])

  const onClickSave = async () => {
    const isValid = await formik.validateForm()
    if (Object.keys(isValid).length === 0 && !addressError.from && !addressError.to) {
      setMode('view')
    }
  }

  const addressString = useCallback(
    (prefix: 'from' | 'to') => {
      return [
        formik.values?.[prefix]?.street1,
        formik.values?.[prefix]?.city,
        formik.values?.[prefix]?.state,
        formik.values?.[prefix]?.zip,
        formik.values?.[prefix]?.country,
      ]
        .filter(Boolean)
        .join(', ')
    },
    [formik.values]
  )

  const onClickEdit = () => {
    if (disabled) return

    setMode('edit')
  }
  const ButtonEdit = () => {
    return (
      <div
        className={clsx('text-primary-200 fw-bold text-decoration-underline flex-shrink-0', {
          'cursor-not-allowed opacity-50': disabled,
          'cursor-pointer': !disabled,
        })}
        onClick={onClickEdit}
      >
        {intl.formatMessage({id: 'EDIT'})}
      </div>
    )
  }
  return (
    <MessageItemWrapper
      message={message}
      wrapperClassName={clsx({
        'w-100': chatbotMode === CHATBOT_MODE.DEFAULT,
        'w-75': chatbotMode !== CHATBOT_MODE.DEFAULT,
      })}
    >
      <div className='d-flex flex-column gap-3 w-100'>
        <div className='row'>
          {isEditMode ? (
            <AutocompleteAddress
              formik={formik}
              prefix='from'
              className='col-8'
              label={`${intl.formatMessage({id: 'SHIP_FROM'})}:`}
              labelClassName='col-4 fw-normal mt-4 text-dark fs-base'
              placeholder={intl.formatMessage({id: 'ORDER_INFO_ADDRESS_PLACEHOLDER'})}
              required={false}
              addressError={addressError.from}
              setAddressError={(errorMessage: string) => setAddressError('from', errorMessage)}
            />
          ) : (
            <>
              <div className='col-4'>{intl.formatMessage({id: 'SHIP_FROM'})}:</div>
              <div className='col-8 fw-bolder d-flex gap-2 justify-content-between'>
                {addressString('from')}
                {ButtonEdit()}
              </div>
            </>
          )}
        </div>
        <div className='row'>
          {isEditMode ? (
            <AutocompleteAddress
              formik={formik}
              prefix='to'
              className='col-8'
              label={`${intl.formatMessage({id: 'SHIP_TO'})}:`}
              labelClassName='col-4 fw-normal mt-4 text-dark fs-base'
              placeholder={intl.formatMessage({id: 'ORDER_INFO_ADDRESS_PLACEHOLDER'})}
              required={false}
              addressError={addressError.to}
              setAddressError={(errorMessage: string) => setAddressError('to', errorMessage)}
            />
          ) : (
            <>
              <div className='col-4'>{intl.formatMessage({id: 'SHIP_TO'})}:</div>
              <div className='col-8 fw-bolder d-flex gap-2 justify-content-between'>
                {addressString('to')}
                {ButtonEdit()}
              </div>
            </>
          )}
        </div>
        <div className='row'>
          <div className='col-4'>{intl.formatMessage({id: 'DIMENSIONS'})}:</div>
          <div className='col-8 fw-bolder'>
            {`${formik.values.dimension[0].length} x ${formik.values.dimension[0].width} x ${formik.values.dimension[0].height} ${formik.values.dimension[0].dimension_unit}`}
          </div>
        </div>
        <div className='row'>
          <div className='col-4'>{intl.formatMessage({id: 'WEIGHT'})}:</div>
          <div className='col-8 fw-bolder'>
            {`${formik.values.dimension[0].weight} ${formik.values.dimension[0].weight_unit}`}
          </div>
        </div>
      </div>
      {
        <div className='d-flex justify-content-end gap-4 mt-4'>
          {isEditMode ? (
            <>
              <Button
                event={() => {
                  setMode('view')
                  resetForm()
                }}
                className='btn btn-sm btn-advanced-secondary'
              >
                {intl.formatMessage({id: 'CANCEL'})}
              </Button>
              <Button className='btn btn-sm btn-advanced-primary' event={onClickSave}>
                {intl.formatMessage({id: 'SAVE'})}
              </Button>
            </>
          ) : (
            <Button
              className='btn btn-sm bg-primary-200 text-white'
              event={formik.handleSubmit}
              disabled={disabled}
            >
              {intl.formatMessage({id: 'CONFIRM_DETAILS'})}
            </Button>
          )}
        </div>
      }
    </MessageItemWrapper>
  )
}
